import { useCallback, useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Elements } from '@stripe/react-stripe-js'
import type { Stripe } from '@stripe/stripe-js'
import { Card } from '@/components/ui/Card'
import { Alert } from '@/components/ui/Alert'
import { Spinner } from '@/components/ui/Spinner'
// ⚠️ ONE Stripe loader for the whole app (memory: fix_stripe_single_loader). Never add a
// second loadStripe() call or hand-inject js.stripe.com — that throws the documented
// "Stripe.js loaded more than once" IntegrationError.
import { getStripe } from '@/components/payment/StripePayment'
// The card fields, the confirm button and the plain-English card errors are shared with
// the inline card step on the contract signing page — ONE copy, in one component.
import { CardEntry, money, ordinal, prettyDate } from '@/components/payment/CardMandateFields'
import api from '@/services/api'

/**
 * Standalone "card on file" (continuous payment authority) setup page — the card
 * twin of SetupDirectDebit. An admin sends the customer this link instead of the
 * Direct Debit form when they'd rather pay their monthly amount by card.
 *
 * Deliberately built to look and read exactly like the Direct Debit page: same
 * shell, same logo, same tone — to the customer it is the same company asking the
 * same question, just with a card instead of a bank account.
 *
 * Backend contract (token-scoped, no login):
 *   GET  /card-mandate/{token}               → what they are agreeing to
 *   POST /card-mandate/{token}/setup-intent  → verification PaymentIntent, setup_future_usage=off_session
 *   POST /card-mandate/{token}/confirm       → store the card + record consent
 *
 * The check payment is a liveness check on the card and is refunded straight away; the card
 * itself is vaulted at Stripe. Nothing on this page ever sees a full card number —
 * the number only ever exists inside Stripe's hosted <PaymentElement> iframe.
 */

interface CardMandate {
  status: string
  customer_name: string
  customer_email: string
  order_number: string | null
  monthly_amount: number
  charge_day: number
  consent_text: string
  consent_version: string
  expires_at: string | null
  already_completed: boolean
  // What we actually take to check the card. Quoted by the backend (30p — Stripe's GBP
  // minimum), never hard-coded here: the page said "£0.01" while the charge was 30p.
  verification_amount?: string
}

interface ConfirmResult {
  success: boolean
  reference?: string
  card_brand?: string
  card_last4?: string
  charge_day?: number
  next_charge_date?: string
}

export default function SetupCard() {
  const { token } = useParams<{ token: string }>()

  const [stripe, setStripe] = useState<Stripe | null>(null)
  const [stripeFailed, setStripeFailed] = useState(false)
  const [clientSecret, setClientSecret] = useState('')
  const [intentError, setIntentError] = useState('')
  const [consent, setConsent] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [formError, setFormError] = useState('')
  const [result, setResult] = useState<ConfirmResult | null>(null)
  // Bumped to force a brand-new PaymentElement when the customer tries another card.
  const [attempt, setAttempt] = useState(0)
  const creatingIntent = useRef(false)

  const { data, isLoading, error } = useQuery({
    queryKey: ['card-mandate', token],
    queryFn: async () => (await api.get(`/card-mandate/${token}`)).data as CardMandate,
    enabled: !!token,
    retry: false,
  })

  const CHECK_AMOUNT = data?.verification_amount || '£0.30'

  const expired = data?.status === 'expired'
  const completed = !!data?.already_completed || data?.status === 'completed'
  const canPay = !!data && !expired && !completed

  // ── Stripe.js (shared singleton loader) ───────────────────────────────────
  useEffect(() => {
    let active = true
    let settled = false
    // If Stripe.js is blocked by an ad-blocker it never resolves — say so rather
    // than leaving the customer on a spinner forever.
    const timer = setTimeout(() => { if (active && !settled) setStripeFailed(true) }, 12000)
    getStripe()
      .then((s) => { settled = true; if (active) { setStripe(s); if (!s) setStripeFailed(true) } })
      .catch(() => { settled = true; if (active) setStripeFailed(true) })
    return () => { active = false; clearTimeout(timer) }
  }, [])

  // ── The check PaymentIntent ───────────────────────────────────────────────
  const createIntent = useCallback(async () => {
    // Guard: clearing the client secret re-triggers the effect below, so without
    // this a "try another card" would open two verification intents.
    if (creatingIntent.current) return
    creatingIntent.current = true
    setIntentError('')
    setClientSecret('')
    try {
      const r = await api.post(`/card-mandate/${token}/setup-intent`, {})
      // r.data.publishable_key is returned too, but the key is already owned by the
      // single shared loader — using it here would mean a second loadStripe().
      setClientSecret(r.data?.client_secret || '')
      if (!r.data?.client_secret) setIntentError('We could not open the secure card form. Please try again shortly.')
    } catch (e: any) {
      setIntentError(
        e?.response?.data?.detail ||
        'We could not open the secure card form. Please try again shortly, or contact us and we will help.',
      )
    } finally {
      creatingIntent.current = false
    }
  }, [token])

  useEffect(() => {
    if (canPay && !clientSecret && !intentError) void createIntent()
  }, [canPay, clientSecret, intentError, createIntent])

  // ── Record the mandate once the check payment has been authorised ────────
  const finalise = useCallback(async (paymentIntentId: string) => {
    const r = await api.post(`/card-mandate/${token}/confirm`, {
      payment_intent_id: paymentIntentId,
      consent_given: true,
    })
    setResult(r.data as ConfirmResult)
  }, [token])

  // 3DS that needs a full page redirect brings the customer back here with the
  // PaymentIntent on the query string. Pick it up so they never land on a dead page.
  useEffect(() => {
    if (!token || result) return
    const params = new URLSearchParams(window.location.search)
    const pi = params.get('payment_intent')
    const status = params.get('redirect_status')
    if (!pi) return
    window.history.replaceState({}, '', window.location.pathname)
    if (status && status !== 'succeeded') {
      setFormError('The security check with your bank was not completed, so nothing has been set up. Please try again.')
      return
    }
    setSubmitting(true)
    finalise(pi)
      .catch((e: any) => setFormError(
        e?.response?.data?.detail || 'Your card was authorised but we could not finish the setup. Please contact us before trying again.',
      ))
      .finally(() => setSubmitting(false))
  }, [token, result, finalise])

  const tryAnotherCard = () => {
    setFormError('')
    setAttempt((a) => a + 1)
    void createIntent()
  }

  const shell = (children: React.ReactNode) => (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <Card className="max-w-md w-full p-6">
        <div className="text-center mb-4">
          <img src="/images/silux_mobile_logo.png" alt="Silux Mobile" className="h-12 w-auto mx-auto mb-3" />
          <p className="text-sm text-gray-500">Monthly Card Payment Setup</p>
        </div>
        {children}
      </Card>
    </div>
  )

  if (isLoading) return shell(<div className="text-center py-8"><Spinner size="lg" /></div>)

  if (error || !data) {
    return shell(
      <Alert variant="error">This card setup link is invalid or has expired. Please contact support.</Alert>,
    )
  }

  // ── Done ──────────────────────────────────────────────────────────────────
  if (result?.success) {
    const brand = (result.card_brand || '').replace(/\b\w/g, (c) => c.toUpperCase())
    const day = result.charge_day || data.charge_day
    return shell(
      <div className="py-2">
        <div className="text-center">
          <div className="text-green-500 text-4xl mb-2">✅</div>
          <h2 className="text-lg font-semibold text-gray-900">Card set up</h2>
          <p className="text-sm text-gray-600 mt-1">
            Thank you. Your monthly payment is now in place; nothing more to do.
          </p>
        </div>
        <div className="mt-4 rounded-lg border bg-gray-50 p-3 text-sm text-gray-700 space-y-1">
          <div className="flex justify-between"><span className="text-gray-500">Card</span>
            <span className="font-medium">{brand || 'Card'} ending {result.card_last4 || '••••'}</span></div>
          <div className="flex justify-between"><span className="text-gray-500">Monthly amount</span>
            <span className="font-medium">{money(data.monthly_amount)}</span></div>
          <div className="flex justify-between"><span className="text-gray-500">Taken on</span>
            <span className="font-medium">the {ordinal(day)} of each month</span></div>
          {result.next_charge_date && (
            <div className="flex justify-between"><span className="text-gray-500">First payment</span>
              <span className="font-medium">{prettyDate(result.next_charge_date)}</span></div>
          )}
          {result.reference && (
            <div className="flex justify-between"><span className="text-gray-500">Reference</span>
              <span className="font-mono text-xs">{result.reference}</span></div>
          )}
        </div>
        <p className="text-xs text-gray-500 mt-3">
          The {CHECK_AMOUNT} check payment has been refunded. We hold only the last 4 digits and the
          expiry of your card — the full number stays with Stripe, our payment provider. To change the
          card or stop the monthly payment, email <b>support@siluxmobile.co.uk</b> or call <b>01246 912899</b>.
        </p>
      </div>,
    )
  }

  if (completed) {
    return shell(
      <div className="text-center py-4">
        <div className="text-green-500 text-4xl mb-2">✅</div>
        <h2 className="text-lg font-semibold text-gray-900">Card already set up</h2>
        <p className="text-sm text-gray-600 mt-1">
          This link has already been used, so there is nothing more to do. Your monthly payment of{' '}
          {money(data.monthly_amount)} is taken on the {ordinal(data.charge_day)} of each month. If that
          is not right, email <b>support@siluxmobile.co.uk</b> or call <b>01246 912899</b>.
        </p>
      </div>,
    )
  }

  if (expired) {
    return shell(
      <>
        <Alert variant="error">
          This card setup link has expired{data.expires_at ? ` (it ran out on ${prettyDate(data.expires_at)})` : ''}.
        </Alert>
        <p className="text-sm text-gray-600 mt-3">
          Nothing has been charged and no card has been stored. Email <b>support@siluxmobile.co.uk</b> or
          call <b>01246 912899</b> and we will send you a fresh link.
        </p>
      </>,
    )
  }

  // ── The ask ───────────────────────────────────────────────────────────────
  const dayText = `${ordinal(data.charge_day)} of each month`

  return shell(
    <>
      <p className="text-sm text-gray-600 mb-4">
        Hello {data.customer_name || 'there'} — please add the card you would like your monthly payment
        {data.order_number ? ` for order ${data.order_number}` : ''} to come from. It is entered directly
        into our payment provider's secure form below.
      </p>

      {/* Plain-English summary. This sits ABOVE the card form on purpose: nobody
          should have to open a consent box to find out what is being taken, when,
          or what happens to their card number. */}
      <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900">
        <p className="font-semibold mb-2">What you are agreeing to</p>
        <ul className="space-y-2 list-disc pl-4">
          <li>
            We take a <b>{CHECK_AMOUNT} check payment</b> now, purely to confirm the card is live.
            <b> It is refunded straight away</b> — the penny comes back to you.
          </li>
          <li>
            Your card is then <b>stored securely with Stripe</b>, our payment provider.
            <b> Silux Mobile only keeps the last 4 digits and the expiry date</b> — we never see or
            store your full card number.
          </li>
          <li>
            We will charge <b>{money(data.monthly_amount)} a month</b>, on the <b>{dayText}</b>. If a
            month is too short for that date, we take it on the last day of that month instead.
          </li>
          <li>
            This <b>repeats every month until your contract ends, or until you tell us to stop</b>.
          </li>
          <li>
            To stop it, or to change the card, email <b>support@siluxmobile.co.uk</b>, call{' '}
            <b>01246 912899</b>, or ask us in your account — we will confirm in writing. Stopping the
            card payment does not cancel your contract, so anything still owed remains due.
          </li>
        </ul>
      </div>

      {/* The authoritative wording is whatever the backend records against the
          consent version — render it, never a copy of our own. */}
      {data.consent_text && (
        <div className="mt-3 bg-gray-50 rounded-lg p-3 max-h-44 overflow-y-auto text-xs text-gray-600 whitespace-pre-line">
          {data.consent_text}
          {data.consent_version && (
            <div className="mt-2 text-[11px] text-gray-400">Version {data.consent_version}</div>
          )}
        </div>
      )}

      <label className="flex items-start gap-2 mt-3 text-sm text-gray-800 cursor-pointer">
        <input
          type="checkbox"
          className="mt-1 h-4 w-4"
          checked={consent}
          onChange={(e) => setConsent(e.target.checked)}
        />
        <span>
          I authorise Silux Mobile to take a {CHECK_AMOUNT} check payment now (refunded straight away),
          to store this card securely with Stripe — keeping only the last 4 digits and expiry themselves,
          never the full number — and to charge {money(data.monthly_amount)} on the {dayText} until my
          contract ends or I tell them to stop. I know I can stop it at any time by contacting Silux Mobile.
        </span>
      </label>

      <div className="mt-4">
        {stripeFailed || (!stripe && intentError) ? (
          <Alert variant="error">
            We couldn't load the secure card form. This is almost always an ad-blocker or privacy
            extension blocking our payment provider — please pause it and refresh, or try another
            browser. You can also call us on 01246 912899 and we will take the details over the phone.
          </Alert>
        ) : intentError ? (
          <Alert variant="error">{intentError}</Alert>
        ) : !stripe || !clientSecret ? (
          <div className="text-center py-6"><Spinner size="lg" /></div>
        ) : (
          <Elements
            key={`${clientSecret}-${attempt}`}
            stripe={stripe}
            options={{
              clientSecret,
              appearance: {
                theme: 'stripe' as const,
                variables: {
                  colorPrimary: '#40A345',
                  colorText: '#1f2937',
                  colorDanger: '#ef4444',
                  fontFamily: 'system-ui, -apple-system, sans-serif',
                  borderRadius: '0.5rem',
                },
              },
            }}
          >
            <CardEntry
              amountLabel={CHECK_AMOUNT}
              consent={consent}
              submitting={submitting}
              setSubmitting={setSubmitting}
              formError={formError}
              setFormError={setFormError}
              onAuthorised={finalise}
              onTryAnother={tryAnotherCard}
            />
          </Elements>
        )}
      </div>

      <p className="text-[11px] text-gray-400 mt-4 text-center">
        Card details are entered directly into Stripe's secure form. They never touch Silux Mobile's
        systems.
      </p>
    </>,
  )
}
